import type { LucideIcon } from "lucide-preact";
import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

type DropdownProps = {
  trigger: ComponentChildren;
  children: ComponentChildren | ((closeDropdown: () => void) => ComponentChildren);
  align?: "left" | "right";
};

/** Closes the menu on a press outside it and on Escape, only while it is open. */
const useDismiss = (isOpen: boolean, ref: { current: HTMLElement | null }, close: () => void) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onPress = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        close();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };
    document.addEventListener("mousedown", onPress);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPress);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);
};

export const Dropdown = ({ trigger, children, align = "right" }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const close = () => setIsOpen(false);
  useDismiss(isOpen, ref, close);

  const toggle = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div ref={ref} class="relative inline-block">
      <div onClick={toggle}>{trigger}</div>
      {isOpen && (
        <div
          class={`absolute top-full mt-1 ${
            align === "left" ? "left-0" : "right-0"
          } z-50 w-max bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-gray-200 dark:border-neutral-700 py-1 animate-in fade-in slide-in-from-top-2 duration-200`}
        >
          {typeof children === "function" ? children(close) : children}
        </div>
      )}
    </div>
  );
};

type DropdownItemProps = {
  icon: LucideIcon;
  label: string;
  onClick: (event: MouseEvent) => void;
  isActive?: boolean;
  [dataAttribute: `data-${string}`]: string | undefined;
};

export const DropdownItem = ({ icon: Icon, label, onClick, isActive = false, ...rest }: DropdownItemProps) => {
  const handleClick = (event: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClick(event);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      class="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700/50"
      {...rest}
    >
      <Icon size={18} stroke-width={isActive ? 2.5 : 1.5} class={isActive ? "text-lime-600 dark:text-lime-400" : "text-gray-500 dark:text-gray-400"} />
      <span class="text-xs font-medium">{label}</span>
    </button>
  );
};
